import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, catchError, debounceTime, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { ReportTestItem } from './ci-mapping.model';
import { TestCase } from '../../models/test-case.model';
import { TestCaseService } from '../../services/test-case.service';
import { FIELD_LENGTHS } from '../../shared/field-lengths';

const SEARCH_DEBOUNCE_MS = 250;

let nextId = 0;

/**
 * Selbst gebaut statt `lib-modal`: die veröffentlichte Library-Fassung kennt weder
 * Fokusfalle noch Escape noch `role="dialog"` (gleiche Entscheidung wie
 * `test-run-handover-modal`).
 */
@Component({
  selector: 'app-ci-mapping-assign-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, MatIconModule, TranslatePipe],
  templateUrl: './ci-mapping-assign-dialog.component.html',
  styleUrl: './ci-mapping-assign-dialog.component.scss',
})
export class CiMappingAssignDialogComponent implements OnInit {
  private readonly testCaseService = inject(TestCaseService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly test = input.required<ReportTestItem>();
  readonly classname = input<string | null>(null);
  readonly busy = input(false);
  readonly errorKey = input<string | null>(null);

  readonly picked = output<TestCase>();
  readonly cancelled = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly ids = createIds();

  readonly cases = signal<TestCase[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly activeIndex = signal(-1);

  readonly activeId = computed(() => (this.activeIndex() < 0 ? null : this.ids.option + this.activeIndex()));
  readonly subtitle = computed(() => {
    const classname = this.classname();
    return classname ? `${classname} › ${this.test().name}` : this.test().name;
  });

  private readonly list = viewChild<ElementRef<HTMLElement>>('list');
  private readonly terms$ = new Subject<string>();

  ngOnInit(): void {
    this.terms$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        startWith(''),
        distinctUntilChanged(),
        tap(() => this.loading.set(true)),
        switchMap((term) =>
          this.testCaseService.list(this.projectId(), term).pipe(
            map((cases): TestCase[] | null => cases),
            catchError(() => of(null)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((cases) => {
        this.cases.set(cases ?? []);
        this.loadError.set(cases === null);
        this.loading.set(false);
        this.activeIndex.set(-1);
      });
  }

  onSearch(value: string): void {
    this.terms$.next(value);
  }

  onKeydown(event: KeyboardEvent): void {
    const last = this.cases().length - 1;
    const moves: Record<string, () => number> = {
      ArrowDown: () => Math.min(this.activeIndex() + 1, last),
      ArrowUp: () => Math.max(this.activeIndex() - 1, 0),
      Home: () => 0,
      End: () => last,
    };
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const active = this.cases()[this.activeIndex()];
      if (active) this.pick(active);
    } else if (moves[event.key] && last >= 0) {
      event.preventDefault();
      this.activeIndex.set(moves[event.key]());
      this.scrollActiveIntoView();
    }
  }

  pick(testCase: TestCase): void {
    if (!this.busy()) this.picked.emit(testCase);
  }

  cancel(): void {
    if (!this.busy()) this.cancelled.emit();
  }

  private scrollActiveIntoView(): void {
    const id = this.activeId();
    const option = id ? this.list()?.nativeElement.querySelector<HTMLElement>(`#${id}`) : null;
    option?.scrollIntoView({ block: 'nearest' });
  }
}

function createIds() {
  const base = `ci-assign-${nextId++}`;
  return { title: `${base}-title`, search: `${base}-search`, list: `${base}-list`, option: `${base}-option-` };
}
